import React from 'react';
import { useLocation } from 'react-router-dom';

import { useAppContext } from 'core/hooks';
import {
	getSessionStorageItem,
	setSessionStorageItem,
} from 'core/browserStorage';

/**
 *
 * @param sessionStorageKeyToUse
 *
 * The initial goal of this hook is to purposely add a delay in showing
 * authenticated header/footer in the homepage when the conditions are satisifed:
 * 1. Welcome Journey flag is enabled - as this is related to welcome journey
 * 2. The session storage item value is empty - so that it can only be done once
 *
 * This is to give an impression that the welcome journey screens are shown first before the dashboard
 * while making sure that the dashboard components are also loading in the background
 *
 * Ideally, the delay should only happen once after logging in, and it should only
 * delay the `showing of UI` part, not the API calls related to the said components
 *
 */
export const useShowComponentWithDelay = (sessionStorageKeyToUse: string) => {
	const { sitecoreContext } = useAppContext();
	const { pathname } = useLocation();

	const [isToShow, setIsToShow] = React.useState(false);

	React.useEffect(() => {
		const isInHomePage = pathname === '/';

		if (isInHomePage) {
			const isWelcomeJourneyEnabled =
				sitecoreContext?.featureToggle?.enableWelcomeJourney ?? false;
			const isToDelay = getSessionStorageItem(sessionStorageKeyToUse) === '';

			if (isWelcomeJourneyEnabled && isToDelay) {
				const timeoutId = setTimeout(() => {
					setSessionStorageItem(sessionStorageKeyToUse, '1');
					setIsToShow(true);
				}, 1500);

				return () => clearTimeout(timeoutId);
			}
		}

		setIsToShow(true);
	}, []);

	return { isToShow };
};
